import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ScrollReveal } from '@/components/ui/ScrollReveal';

export function CaseStudies() {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const caseStudies = [
    {
      id: 1,
      metric: '+40%',
      metricLabel: t('cases.case1.metric', 'Cross-Functional Collaboration'),
      client: t('cases.case1.client', 'Global Technology Enterprise'),
      challenge: t('cases.case1.challenge', 'Siloed engineering teams were experiencing low morale and missed delivery cycles due to chronic miscommunication and protective behaviors.'),
      solution: t('cases.case1.solution', 'Implemented the Social Drives and Enneagram lenses to uncover hidden motivational blocks, restructure team dynamics, and rebuild psychological safety.'),
      quote: "We went from defensive standoffs to genuinely curious problem-solving in a matter of weeks.",
    },
    {
      id: 2,
      metric: '3x',
      metricLabel: t('cases.case2.metric', 'Increase in Leadership Readiness'),
      client: t('cases.case2.client', 'Financial Services Firm'),
      challenge: t('cases.case2.challenge', 'A critical gap in emotional agility and strategic decision-making among mid-level managers preparing for executive transition.'),
      solution: t('cases.case2.solution', 'Leveraged Personal Mastery and Energy Centres lenses in targeted 1-on-1 coaching to align the Instinctual, Emotional and Intellectual centres under pressure.'),
      quote: "Our emerging leaders finally have the emotional vocabulary to navigate high-stakes conflict.",
    },
    {
      id: 3,
      metric: '-35%',
      metricLabel: t('cases.case3.metric', 'Reduction in Key Talent Turnover'),
      client: t('cases.case3.client', 'National Healthcare Provider'),
      challenge: t('cases.case3.challenge', 'Severe post-merger cultural friction leading to high attrition, change-fatigue, and plummeting engagement scores across departments.'),
      solution: t('cases.case3.solution', 'Used the Emotional Resilience lens to map stress recovery capacities, equipping leaders to navigate disruption and foster a unified, empathetic culture.'),
      quote: "FIVE LENS didn't just give us a report; it gave us a shared language for healing.",
    },
    {
      id: 4,
      metric: '+60%',
      metricLabel: t('cases.case4.metric', 'Team Alignment & Trust'),
      client: t('cases.case4.client', 'Retail Logistics Firm'),
      challenge: t('cases.case4.challenge', 'Executive board was frequently deadlocked on strategic initiatives due to interpersonal friction and lack of mutual trust.'),
      solution: t('cases.case4.solution', 'Facilitated deep-dive workshops using the Enneagram and Social Drives lenses to de-personalize conflict and build mutual understanding.'),
      quote: "We finally understand each other's operating systems. The friction is gone.",
    },
    {
      id: 5,
      metric: '2.5x',
      metricLabel: t('cases.case5.metric', 'Faster Post-Merger Integration'),
      client: t('cases.case5.client', 'International FMCG Brand'),
      challenge: t('cases.case5.challenge', 'Merging two distinct corporate cultures resulted in severe operational bottlenecks and middle-management resistance.'),
      solution: t('cases.case5.solution', 'Deployed the full FIVE LENS suite to map cultural pain points, rapidly aligning management styles and accelerating the integration timeline.'),
      quote: "The speed at which we achieved true synergy was entirely thanks to the FIVE LENS insights.",
    }
  ];

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth > 768 ? 600 : 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="case-studies" className="py-24 bg-white border-t border-ground-deep overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-8">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-[1px] w-8 bg-rule-3"></span>
                <span className="text-[11px] uppercase tracking-[0.3em] font-bold text-rule-3">{t('cases.eyebrow', 'Proven Impact')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-serif text-ink leading-tight">
                {t('cases.title', 'Improving corporate culture and performance.')}
              </h2>
            </div>
            
            <div className="flex items-center gap-4">
              <button 
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className="w-12 h-12 rounded-full border border-ground-deep flex items-center justify-center text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous case study"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className="w-12 h-12 rounded-full border border-ground-deep flex items-center justify-center text-ink hover:bg-ink hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next case study"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </ScrollReveal>

        <div className="relative -mx-6 lg:-mx-8 px-6 lg:px-8">
          <div 
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-12 pt-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {caseStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="snap-start shrink-0 w-[85vw] md:w-[600px] lg:w-[480px] bg-ground/30 border border-ground-deep p-8 lg:p-10 hover:bg-white hover:shadow-xl hover:shadow-ink/10 hover:border-ink transition-all hover:-translate-y-1 duration-500 flex flex-col group relative"
              >
                <div className="absolute top-8 right-8 text-ground-deep group-hover:text-rule-3/20 transition-colors">
                  <Quote className="w-12 h-12 rotate-180" />
                </div>

                <div className="mb-8">
                  <div className="text-5xl font-serif text-rule-3 mb-3 flex items-start gap-1">
                    {study.metric}
                    <ArrowUpRight className="w-5 h-5 text-rule-3 opacity-50 mt-2" />
                  </div>
                  <div className="text-sm font-bold uppercase tracking-widest text-ink max-w-[200px] leading-relaxed">
                    {study.metricLabel}
                  </div>
                </div>
                
                <div className="mb-8 flex-grow">
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-ink/70/50 mb-4">
                    {study.client}
                  </div>
                  <div className="space-y-6">
                    <div>
                      <strong className="block text-sm font-bold text-ink mb-2">{t('cases.labels.challenge', 'The Challenge')}</strong>
                      <p className="text-ink/70/80 text-sm leading-relaxed font-light">
                        {study.challenge}
                      </p>
                    </div>
                    <div>
                      <strong className="block text-sm font-bold text-ink mb-2">{t('cases.labels.solution', 'The FIVE LENS Solution')}</strong>
                      <p className="text-ink/70/80 text-sm leading-relaxed font-light">
                        {study.solution}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto pt-6 border-t border-ground-deep">
                  <p className="text-[15px] italic font-serif text-ink/80 leading-relaxed">
                    "{study.quote}"
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
